<template>
  <b-modal
    :id="`mc-modal-view-proof-${activity?.id}`"
    ref="mc-modal-view-proof"
    :title="title"
    class="mc-modal"
    size="lg"
    centered
    @show="handleModalShow"
  >
    <div class="card-body p-2">
      <div>
        <div class="info-row text-muted">
          <span class="info-label">{{ contactPersonLabel }}</span>
          <span class="info-separator">:</span>
          <span class="info-value">{{ activityDetail?.contact_person_name || '-' }}</span>
        </div>
        <div class="info-row text-muted">
          <span class="info-label">{{ completedOnLabel }}</span>
          <span class="info-separator">:</span>
          <span class="info-value">{{
            activityDetail?.completed_on ? formatDateWithTz(activityDetail.completed_on) : '-'
          }}</span>
        </div>
        <div class="info-row text-muted">
          <span class="info-label">{{ $t('fleetTask.viewProof.fields.note') }}</span>
          <span class="info-separator">:</span>
          <span class="info-value">{{ activityDetail?.note || '-' }}</span>
        </div>
        <div class="info-row text-muted">
          <span class="info-label">{{ $t('fleetTask.viewProof.fields.additionalNote') }}</span>
          <span class="info-separator">:</span>
          <McChipList
            v-if="notes && notes.length"
            :items="notes"
            :max-visible="maxVisibleNotes"
            v-bind="{ itemTextField: 'note', itemAttachmentUrlField: 'document_attachment_url' }"
          />
          <span v-else>-</span>
        </div>
      </div>

      <div v-if="activityDetail?.attachments && activityDetail.attachments.length > 0">
        <p class="text-muted mb-2">{{ $t('fleetTask.viewProof.fields.photo') }}</p>
        <div class="attachments-wrapper">
          <div
            v-for="(attachment, index) in activityDetail.attachments"
            :key="index"
            class="attachment"
          >
            <div class="attachment-img" @click="handleOpenPreview(index)">
              <img
                :src="attachment.url"
                :alt="$t('fleetTask.viewProof.attachmentAlt', { index: index + 1 })"
              />
            </div>
            <button
              class="download-btn"
              @click="
                handleDownloadImg(
                  attachment.url,
                  $t('fleetTask.viewProof.attachmentFileName', { index: index + 1 })
                )
              "
            >
              <i class="icon-outline_download"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <div class="d-flex mx-0">
        <McButton
          variant="primary"
          class="me-2"
          :text="$t('global.action.close')"
          @click="handleModalCancel"
        />
      </div>
    </template>

    <PreviewImage
      v-if="activeAttachmentIdx !== null"
      :attachments="activityDetail?.attachments"
      :activeAttachmentIdx="activeAttachmentIdx"
      @update:activeAttachmentIdx="activeAttachmentIdx = $event"
      @handleDownloadImg="handleDownloadImg"
    />
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import McButton from '@/components/McButton'
import McChipList from '@/components/McChipList'
import { formatDateWithTz } from '@/helpers/tzDate'
import * as FleetTaskActivity from '@/services/rest-api/lib/fleet-task/activity'
import PreviewImage from '@/router/views/delivery/components/PreviewImage.vue'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    McButton,
    McChipList,
    PreviewImage,
  },
  props: {
    activity: {
      type: Object,
      default: () => ({}),
    },
    fleetTaskId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      activityDetail: null,
      activeAttachmentIdx: null,
      maxVisibleNotes: 3,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isPickUp() {
      return this.activity.type == 'PICKUP'
    },
    title() {
      return this.$t('fleetTask.viewProof.title', {
        proofLabel: this.proofLabel,
        deliveryNumber: this.activity.delivery_order?.number,
      })
    },
    proofLabel() {
      return this.$t(
        this.isPickUp ? 'fleetTask.viewProof.activity.pickup' : 'fleetTask.viewProof.activity.delivery'
      )
    },
    contactPersonLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.viewProof.fields.senderName'
          : 'fleetTask.viewProof.fields.recipientName'
      )
    },
    completedOnLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.viewProof.fields.pickupDate'
          : 'fleetTask.viewProof.fields.deliveryDate'
      )
    },
    notes() {
      if (!this.activityDetail) return []
      const raw = this.activityDetail.customer_notes
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        return raw.length
          ? raw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : []
      }
      return []
    },
    visibleCustomerNotes() {
      return this.notes
    },
  },
  methods: {
    formatDateWithTz,
    handleModalCancel() {
      this.$bvModal.hide(`mc-modal-view-proof-${this.activity.id}`)
      this.activeAttachmentIdx = null
    },
    async handleModalShow() {
      this.isLoading = true
      this.activeAttachmentIdx = null

      try {
        const response = await FleetTaskActivity.getActivity(
          this.fleetTaskId,
          this.activity.delivery_order.id
        )

        if (response && response.data && response.data.length > 0) {
          const activityType = this.isPickUp ? 'PICKUP' : 'DROP'
          this.activityDetail = response.data.find((item) => item.type === activityType) || null
        }
      } catch (error) {
        console.error(error)
        this.activityDetail = null
      } finally {
        this.isLoading = false
      }
    },
    async handleDownloadImg(imgUrl, fileName) {
      try {
        const res = await fetch(imgUrl, {
          headers: new Headers({
            'Cache-Control': 'no-cache',
          }),
        })
        if (!res.ok) return

        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error(this.$t('fleetTask.viewProof.log.downloadError'), error)
      }
    },
    handleOpenPreview(idx) {
      this.activeAttachmentIdx = idx
    },
  },
}
</script>

<style scoped lang="scss">
.info-row {
  display: flex;
  margin-bottom: 4px;
}

.info-label {
  width: 150px;
  flex-shrink: 0;
}

.info-separator {
  margin: 0 8px;
}

.info-value {
  flex-grow: 1;
}

.attachments-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.attachment {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  margin-bottom: 10px;
}

.attachment-img {
  border-radius: 4px;
  height: 100px;
  width: 100px;
  overflow: hidden;
  cursor: pointer;
}

.attachment-img img {
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.attachment-name {
  margin: 0;
  font-size: 12px;
  color: #26a69a;
}

.download-btn {
  position: absolute;
  top: 3px;
  right: 3px;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid $blue;
  border-radius: 3px;
  background-color: #ffffff;
  padding: 0;
}

.download-btn i::before {
  font-size: 16px;
  color: $blue;
}

.info-value-customer-notes {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.notes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
}

.note-chip {
  display: inline-flex;
  align-items: center;
  background: $blue;
  color: $white;
  border-radius: 18px;
  font-size: 12px;
  overflow: hidden;
  height: 25px;
}

/* text area */
.note-text {
  padding: 0 14px;
  display: flex;
  align-items: center;
}

/* icon area kanan */
.note-attachment {
  width: 34px;
  height: 100%;
  border: none;
  border-left: 2px solid $white;
  background: $blue;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.note-attachment i::before {
  font-size: 16px;
  color: $white;
}

.note-chip.more-count {
  background: $green;
  min-width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  padding: 0 8px;
  font-weight: 600;
}

.notes-actions {
  margin-top: 6px;
  width: 100%;
}

.toggle-notes {
  background: transparent;
  border: none;
  color: $cyan;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.notes-actions .toggle-notes {
  display: inline-block;
  margin-left: 0;
}

.chev::before {
  position: absolute;
  font-size: 16px;
  padding-top: 2px !important;
  font-weight: 500;
}
</style>
